/**
 * Who belongs on the unified parent/athlete dashboard surface (/dashboard/parent).
 *
 * After the U6 dashboard unification, self-managed athletes (userType 'athlete')
 * no longer have a standalone /dashboard/athlete shell — they share this surface,
 * and the /dashboard hub routes both 'parent' and 'athlete' here. The parent
 * dashboard's own guard must agree: if it redirects athletes away, the athlete and
 * the hub ping-pong between /dashboard and /dashboard/parent forever (Safari throttles
 * history.replaceState >100×/10s → SecurityError; every browser thrashes the URL),
 * leaving self-managed athletes unable to use their dashboard (#1186).
 *
 * Staff have their own shell, and 'unknown' is sent back to the hub to be re-routed.
 */
import { calculateAge } from '@/utils/athletes/athleteUtils';
import { isAdultAthleteAge } from '@/utils/invitations/athleteInvitationAge';

export type DashboardUserType = 'staff' | 'parent' | 'athlete' | 'unknown';

export function belongsOnParentDashboard(
  userType: DashboardUserType | string | undefined | null
): boolean {
  return userType === 'parent' || userType === 'athlete';
}

/**
 * An athlete login that is not self-managed: one a parent invited (teen 13-17 or
 * 18+). It sees its own athlete card, but adding athletes, joining a team,
 * answering a placement and paying stay with the parent.
 */
export function isInvitedAthleteLogin(
  user: { userType?: DashboardUserType | string | null; isSelfManaged?: boolean | null } | null | undefined
): boolean {
  return user?.userType === 'athlete' && !user?.isSelfManaged;
}

/**
 * An invited athlete login under 18 (or with no readable birth date). Their
 * profile is view-only and the parent edits it; an invited 18+ athlete edits
 * their own details, as adult athletes usually do.
 */
export function isInvitedTeenAthleteLogin(
  user: Parameters<typeof isInvitedAthleteLogin>[0],
  dateOfBirth: string | null | undefined
): boolean {
  if (!isInvitedAthleteLogin(user)) return false;
  return !(dateOfBirth && isAdultAthleteAge(calculateAge(dateOfBirth)));
}
